import {
  faBook,
  faChevronDown,
  faChevronUp,
  faEdit,
  faInfoCircle,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import {
  faDiamondExclamation,
  faFireFlame
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GlobalPropertiesSelect,
  PropertiesStore
} from '@hortplus/properties-react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Collapse,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip
} from 'react-bootstrap';
import InsectGroupSelect from './InsectGroupSelect';
import TraplogLegend from './TraplogLegend';
import AddTrapModal from './admin/AddTrapModal';
import AddTrapMonitoringRecordsModal from './admin/AddTrapMonitoringRecordsModal';
import AddTrappingZoneModal from './admin/AddTrappingZoneModal';
import EditTrapModal from './admin/EditTrapModal';
import EditTrappingZoneModal from './admin/EditTrappingZoneModal';
import TrappingZoneResults from './admin/TrappingZoneResults';
import TrapsTable from './admin/TrapsTable';
import AddTrappingZoneSprayModal from './admin/add_trapping_zone_spray_modal/AddTrappingZoneSprayModal';
import SetupTrapsModal from './admin/setup_traps_modal/SetupTrapsModal';
import ConfirmationModal from './helpers/ConfirmationModal';
import { ToastContainer } from './helpers/ToastUtils';
import { fetchTrapCSV } from './helpers/TrappingUtils';
import { useCollapse } from './hooks/useCollapse';
import { useStores } from './stores/useStores';

const TrappingZoneTitle = (props) => {
  const { traplogStore } = useStores();

  return (
    <h5 className='my-auto'>
      {props.trappingZone.name}
      {props.trappingZone.cmsm ? ' (CMSM)' : ''}
      {props.trappingZone.mating_disruption_tie ? (
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip>{props.trappingZone.mating_disruption_tie.name}</Tooltip>
          }
        >
          <FontAwesomeIcon
            className='ml-2'
            style={{ marginBottom: '1px' }}
            icon={faInfoCircle}
            size='xs'
          />
        </OverlayTrigger>
      ) : null}
      {traplogStore.isTrappingZoneHighRisk(props.trappingZone) ? (
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip>High Risk Site</Tooltip>}
        >
          <FontAwesomeIcon className='ml-2' icon={faDiamondExclamation} />
        </OverlayTrigger>
      ) : null}
      {traplogStore.isTrappingZoneHighPressure(props.trappingZone) ? (
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip>High Pressure Site</Tooltip>}
        >
          <FontAwesomeIcon className='ml-2' icon={faFireFlame} />
        </OverlayTrigger>
      ) : null}
    </h5>
  );
};

const NZAPOverview = (props) => {
  const { traplogStore } = useStores();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTrappingZoneId, setEditingTrappingZoneId] = useState(null);
  const [editingTrapId, setEditingTrapId] = useState(null);
  const [trapsData, setTrapsData] = useState();
  const [trapsOpen, trapsHandleCollapse] = useCollapse(
    'nzapoverview-traps-open'
  );
  const addTrappingZoneOptions = props.customOptions?.addTrappingZone;
  const editTrappingZoneOptions = props.customOptions?.editTrappingZone;

  const deleteTrap = useCallback(
    async (trapId) => {
      const res = await fetch(
        `${process.env.REACT_APP_TRAPLOG_API_URL}api/traps/${trapId}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${props.bearerToken}`,
            SeasonalDatabase: props.selectedDatabase
          }
        }
      );
      if (res.ok)
        traplogStore.loadTrappingZones(
          props.bearerToken,
          props.refreshBearerToken,
          props.selectedDatabase,
          props.propertiesStore.selectedPropertyId
        );
      else if (res.status === 401) props.refreshBearerToken();
      else throw Error(res.statusText);
    },
    [
      props.bearerToken,
      props.selectedDatabase,
      props.refreshBearerToken,
      props.propertiesStore.selectedPropertyId
    ]
  );

  const trappingZone = useCallback(
    (trappingZoneId) =>
      traplogStore.trappingZones.find(
        (trappingZone) => trappingZone.id === trappingZoneId
      ),
    [traplogStore.trappingZones]
  );

  useEffect(() => {
    if (traplogStore.traps) {
      const newTrapsData = traplogStore.traps
        .map((trap) => {
          const currentTrappingZone = trappingZone(trap.trapping_zone_id);
          return {
            trappingZone: currentTrappingZone,
            area: currentTrappingZone.name,
            code: trap.code,
            name: trap.name,
            location_description: trap.location_description,
            setup_at: trap.setup_at,
            comments: trap.comments,
            edit: (
              <Button
                variant='link'
                type='button'
                className='text-primary'
                onClick={() => setEditingTrapId(trap.id)}
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            ),
            delete: (
              <ConfirmationModal
                title='Confirm Delete'
                bodyText='Are you sure you want to delete this trap?'
                confirmAction={() => {
                  return deleteTrap(trap.id);
                }}
                showButton={{
                  children: <FontAwesomeIcon icon={faTrash} />,
                  props: { className: 'text-danger', variant: 'link' }
                }}
                confirmButton={{ props: { variant: 'danger' } }}
              />
            )
          };
        })
        .sort((a, b) => {
          const trappingZoneA = a.trappingZone;
          const trappingZoneB = b.trappingZone;
          if (trappingZoneA?.name < trappingZoneB?.name) return -1;
          if (trappingZoneA?.name > trappingZoneB?.name) return 1;
          return 0;
        });
      setTrapsData(newTrapsData);
    }
  }, [traplogStore.traps, trappingZone, deleteTrap]);

  const deleteTrappingZone = async (trappingZoneId) => {
    const res = await fetch(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-zones/${trappingZoneId}`,
      {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${props.bearerToken}`,
          SeasonalDatabase: props.selectedDatabase
        }
      }
    );
    if (res.ok)
      traplogStore.loadTrappingZones(
        props.bearerToken,
        props.refreshBearerToken,
        props.selectedDatabase,
        props.propertiesStore.selectedPropertyId
      );
    else if (res.status === 401) props.refreshBearerToken();
    else throw Error(res.statusText);
  };

  const selectedTrappingZoneResultsValidation =
    traplogStore.selectedTrappingZoneResults?.validation;
  const selectedTrappingZoneBiofixValidation =
    selectedTrappingZoneResultsValidation?.biofix;

  return (
    <React.Fragment>
      <ToastContainer />
      {messages.map((message, i) => (
        <Alert
          key={i}
          variant={message.variant}
          onClose={() => {
            let newMessages = [...messages];
            newMessages.splice(i, 1);
            setMessages(newMessages);
          }}
          dismissible
        >
          {message.message}
        </Alert>
      ))}

      {/* New user alerts */}
      {traplogStore.selectedInsectGroupId === null ? null : !props
          .propertiesStore.loading &&
        props.propertiesStore.selectedPropertyId &&
        !traplogStore.loadingInsectGroups &&
        !traplogStore.loadingTrappingZones &&
        !traplogStore.loadingTrappingResults &&
        !loading &&
        traplogStore.trappingZones.length === 0 ? (
        <Row className='mb-2'>
          <Col sm={0} md={1}></Col>
          <Col>
            <Alert variant='warning'>
              <p className=' text-center mb-0' style={{ fontSize: '14px' }}>
                Add Production Site First
              </p>
            </Alert>
          </Col>
          <Col sm={0} md={1}></Col>
        </Row>
      ) : traplogStore.selectedTrappingZone?.traps.length === 0 ? (
        <Row className='mb-2'>
          <Col sm={0} md={1}></Col>
          <Col>
            <Alert variant='warning'>
              <p className=' text-center mb-0' style={{ fontSize: '14px' }}>
                Add Traps to Production Site
              </p>
            </Alert>
          </Col>
          <Col sm={0} md={1}></Col>
        </Row>
      ) : null}

      {/* Options box */}
      <Row className='mb-2 justify-content-center'>
        <Col
          xs={3}
          sm={2}
          className='d-flex align-items-center justify-content-end pr-0 font-weight-bold'
        >
          Property:
        </Col>
        <Col xs={9} md={8} lg={6}>
          <GlobalPropertiesSelect
            selectedDatabase={props.selectedDatabase}
            bearerToken={props.bearerToken}
            refreshBearerToken={props.refreshBearerToken}
            propertiesStore={props.propertiesStore}
            useRpin
          />
        </Col>
        <Col
          xs={0}
          sm={1}
          className='d-flex align-items-center justify-content-end pl-0 font-weight-bold'
          style={{ visibility: 'hidden' }}
        >
          Property:
        </Col>
      </Row>
      <Row className='justify-content-center'>
        <Col
          xs={3}
          sm={2}
          className='d-flex align-items-center justify-content-end pr-0 font-weight-bold'
        >
          Insect:
        </Col>
        <Col xs={9} md={8} lg={6}>
          <InsectGroupSelect
            selectedDatabase={props.selectedDatabase}
            bearerToken={props.bearerToken}
            refreshBearerToken={props.refreshBearerToken}
            customInsects={['CM', 'CM SIT', 'LBAM', 'LBAM SIT']}
          />
        </Col>
        <Col
          xs={0}
          sm={1}
          className='d-flex align-items-center justify-content-end pl-0 font-weight-bold'
          style={{ visibility: 'hidden' }}
        >
          Insect:
        </Col>
      </Row>

      {traplogStore.selectedInsectGroupId === null ? null : props
          .propertiesStore.loading ||
        traplogStore.loadingInsectGroups ||
        traplogStore.loadingTrappingZones ||
        traplogStore.loadingTrappingResults ||
        loading ? (
        <div className='text-center mt-3'>
          <Spinner variant='primary' animation='border' />
        </div>
      ) : (
        <Fragment>
          {/* Editing trapping zone modal */}
          {editingTrappingZoneId ? (
            <EditTrappingZoneModal
              selectedDatabase={props.selectedDatabase}
              bearerToken={props.bearerToken}
              refreshBearerToken={props.refreshBearerToken}
              propertiesStore={props.propertiesStore}
              trappingZone={traplogStore.trappingZones.find(
                (t) => t.id === editingTrappingZoneId
              )}
              setTrappingZoneId={setEditingTrappingZoneId}
              customOptions={editTrappingZoneOptions}
            />
          ) : null}

          {/* Editing trap modal */}
          {editingTrapId ? (
            <EditTrapModal
              chargedInput={false}
              selectedDatabase={props.selectedDatabase}
              bearerToken={props.bearerToken}
              refreshBearerToken={props.refreshBearerToken}
              propertiesStore={props.propertiesStore}
              trap={traplogStore.traps.find(
                (trap) => trap.id === editingTrapId
              )}
              setTrapId={setEditingTrapId}
            />
          ) : null}

          {/* Trapping Zones */}
          <Row>
            <Col className='text-center'>
              <Row className='mt-1'>
                <Col>
                  {traplogStore.trappingZones
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((trappingZone, i) => {
                      const trappingResult =
                        traplogStore.trappingResults[trappingZone.id];
                      // Set first production site as selected.
                      if (
                        traplogStore.selectedTrappingZoneId === null &&
                        trappingZone &&
                        i === 0
                      ) {
                        traplogStore.setSelectedTrappingZoneId(trappingZone.id);
                      }
                      const variantColour = `${
                        traplogStore.trappingResults && trappingResult
                          ? trappingResult.overview.status === 'good' ||
                            trappingResult.overview.status === 'sprayed'
                            ? 'success'
                            : trappingResult.overview.status === 'no_data'
                            ? 'secondary'
                            : trappingResult.overview.status ===
                                'pre_trap_catch_threshold_met' ||
                              trappingResult.overview.status ===
                                'accumulation_threshold_met' ||
                              trappingResult.overview.status ===
                                'average_threshold_met'
                            ? 'danger'
                            : trappingResult.overview.status ===
                              'near_accumulation_threshold'
                            ? 'warning'
                            : 'primary'
                          : 'primary'
                      }`;
                      return (
                        <Button
                          key={trappingZone.id}
                          className='mx-2 mt-3'
                          variant={`${
                            traplogStore.selectedTrappingZoneId ===
                            trappingZone.id
                              ? ''
                              : 'outline-'
                          }${variantColour}`}
                          onClick={() =>
                            traplogStore.setSelectedTrappingZoneId(
                              trappingZone.id
                            )
                          }
                        >
                          {`${trappingZone.name}`}
                          {traplogStore.isTrappingZoneHighRisk(trappingZone) ? (
                            <FontAwesomeIcon
                              className='ml-1'
                              icon={faDiamondExclamation}
                            />
                          ) : null}
                          {traplogStore.isTrappingZoneHighPressure(
                            trappingZone
                          ) ? (
                            <FontAwesomeIcon
                              className='ml-1'
                              icon={faFireFlame}
                            />
                          ) : null}
                          {`${trappingZone.cmsm ? ' (CMSM)' : ''}`}
                        </Button>
                      );
                    })}
                </Col>
              </Row>
              <Row className='mt-3'>
                <Col>
                  <TraplogLegend className='text-center' />
                </Col>
              </Row>
            </Col>
            <Col sm={4} lg={3} className='mt-3'>
              <div className='d-flex flex-column align-items-end'>
                <AddTrappingZoneModal
                  selectedDatabase={props.selectedDatabase}
                  bearerToken={props.bearerToken}
                  refreshBearerToken={props.refreshBearerToken}
                  propertiesStore={props.propertiesStore}
                  customOptions={addTrappingZoneOptions}
                />
                {traplogStore.trappingZones.length > 0 && (
                  <AddTrapModal
                    chargedInput={false}
                    selectedDatabase={props.selectedDatabase}
                    bearerToken={props.bearerToken}
                    refreshBearerToken={props.refreshBearerToken}
                  />
                )}
                <AddTrappingZoneSprayModal
                  addMany
                  trappingResults={traplogStore.trappingResults}
                  propertiesStore={props.propertiesStore}
                  coverageDays={false}
                >
                  <Button type='button' variant='link'>
                    Add Spray Application
                  </Button>
                </AddTrappingZoneSprayModal>
                <SetupTrapsModal
                  propertiesStore={props.propertiesStore}
                  chargedInput={false}
                >
                  <Button type='button' variant='link'>
                    Seasonal Trap Setup
                  </Button>
                </SetupTrapsModal>
              </div>
            </Col>
          </Row>
          {traplogStore.trappingZones.length > 0 && (
            <Fragment>
              {/* Trapping Table */}
              <h5 onClick={() => trapsHandleCollapse()}>
                Traps{'\t'}
                <FontAwesomeIcon
                  icon={trapsOpen ? faChevronDown : faChevronUp}
                  size='xs'
                />
              </h5>
              {trapsData ? (
                <Collapse in={trapsOpen}>
                  <div>
                    <TrapsTable data={trapsData} />
                  </div>
                </Collapse>
              ) : null}

              {/* Trapping Results Table */}
              {traplogStore.selectedTrappingZoneId !== null ? (
                // This condition fixes a bug when switching property and insect group at the same time
                traplogStore.selectedTrappingZoneResults ? (
                  <Row>
                    <Col>
                      <div className='d-flex justify-content-between align-items-center'>
                        <div className='d-flex justify-content-start align-items-center'>
                          <TrappingZoneTitle
                            trappingZone={traplogStore.selectedTrappingZone}
                          />
                          <Button
                            variant='link'
                            onClick={() =>
                              setEditingTrappingZoneId(
                                traplogStore.selectedTrappingZoneId
                              )
                            }
                            className='ml-4'
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <ConfirmationModal
                            title='Confirm Delete'
                            bodyText='Are you sure you want to delete this production site?'
                            confirmAction={() => {
                              return deleteTrappingZone(
                                traplogStore.selectedTrappingZoneId
                              );
                            }}
                            showButton={{
                              children: <FontAwesomeIcon icon={faTrash} />,
                              props: {
                                className: 'text-danger',
                                variant: 'link'
                              }
                            }}
                            confirmButton={{
                              props: { variant: 'danger' }
                            }}
                          />
                        </div>
                        {trapsData && trapsData.length === 0 ? null : (
                          <>
                            {selectedTrappingZoneBiofixValidation?.status ===
                            'missing' ? (
                              <div className='d-flex align-items-center mb-1'>
                                <Alert className='m-0' variant='danger'>
                                  There is no biofix spray for this production
                                  site.
                                </Alert>
                              </div>
                            ) : null}
                            <div className='my-2'>
                              <AddTrapMonitoringRecordsModal
                                propertiesStore={props.propertiesStore}
                                trappingZone={traplogStore.selectedTrappingZone}
                              />
                              <AddTrappingZoneSprayModal
                                propertiesStore={props.propertiesStore}
                                trappingZone={traplogStore.selectedTrappingZone}
                                trappingResults={{
                                  [traplogStore.selectedTrappingZoneId]:
                                    traplogStore.selectedTrappingZoneResults
                                }}
                                coverageDays={false}
                              >
                                <Button type='button' variant='primary'>
                                  <FontAwesomeIcon icon={faBook} /> Add Spray
                                  Application
                                </Button>
                              </AddTrappingZoneSprayModal>
                            </div>
                          </>
                        )}
                      </div>
                      <TrappingZoneResults
                        trappingZone={traplogStore.selectedTrappingZone}
                        trappingZoneResults={
                          traplogStore.selectedTrappingZoneResults
                        }
                        selectedInsectGroup={traplogStore.selectedInsectGroup}
                        propertiesStore={props.propertiesStore}
                        printable={true}
                      />
                    </Col>
                  </Row>
                ) : null
              ) : (
                traplogStore.sortedTrappingZones.map((trappingZone) => {
                  const trappingZoneResultsValidation =
                    traplogStore.trappingResults[trappingZone.id]?.validation;
                  const trappingZoneBiofixValidation =
                    trappingZoneResultsValidation?.biofix;
                  // This condition fixes a bug when switching property and insect group at the same time
                  if (traplogStore.trappingResults[trappingZone.id]) {
                    return (
                      <Row key={trappingZone.id}>
                        <Col>
                          <div className='d-flex justify-content-between align-items-centre'>
                            <div className='d-flex justify-content-start align-items-centre'>
                              <TrappingZoneTitle trappingZone={trappingZone} />
                              <Button
                                variant='link'
                                onClick={() =>
                                  setEditingTrappingZoneId(trappingZone.id)
                                }
                                className='ml-4'
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                              <ConfirmationModal
                                title='Confirm Delete'
                                bodyText='Are you sure you want to delete this production site?'
                                confirmAction={() => {
                                  return deleteTrappingZone(
                                    traplogStore.selectedTrappingZoneId
                                  );
                                }}
                                showButton={{
                                  children: <FontAwesomeIcon icon={faTrash} />,
                                  props: {
                                    className: 'text-danger',
                                    variant: 'link'
                                  }
                                }}
                                confirmButton={{
                                  props: { variant: 'danger' }
                                }}
                              />
                            </div>
                            {trappingZoneBiofixValidation?.status ===
                            'missing' ? (
                              <div className='d-flex align-items-center mb-1'>
                                <Alert className='m-0' variant='danger'>
                                  There is no biofix spray for this production
                                  site.
                                </Alert>
                              </div>
                            ) : null}
                            <div className='my-2'>
                              <AddTrapMonitoringRecordsModal
                                propertiesStore={props.propertiesStore}
                                trappingZone={trappingZone}
                              />
                              <AddTrappingZoneSprayModal
                                propertiesStore={props.propertiesStore}
                                trappingZone={trappingZone}
                                trappingResults={{
                                  [trappingZone.id]:
                                    traplogStore.trappingResults[
                                      trappingZone.id
                                    ]
                                }}
                                coverageDays={false}
                              >
                                <Button type='button' variant='primary'>
                                  <FontAwesomeIcon icon={faBook} /> Add Spray
                                  Application
                                </Button>
                              </AddTrappingZoneSprayModal>
                            </div>
                          </div>
                          <TrappingZoneResults
                            trappingZone={trappingZone}
                            trappingZoneResults={
                              traplogStore.trappingResults[trappingZone.id]
                            }
                            selectedInsectGroup={
                              traplogStore.selectedInsectGroup
                            }
                            propertiesStore={props.propertiesStore}
                          />
                        </Col>
                      </Row>
                    );
                  } else return null;
                })
              )}
            </Fragment>
          )}
          {traplogStore.trappingResults[traplogStore.selectedTrappingZoneId]
            ?.results.length > 0 ? (
            <Row>
              <Col className='text-center'>
                <Button
                  variant='light'
                  onClick={() => {
                    const result = fetchTrapCSV(
                      traplogStore.bearerToken,
                      traplogStore.selectedDatabase,
                      traplogStore.selectedInsectGroup.short_name,
                      props.propertiesStore.selectedPropertyId,
                      traplogStore.selectedTrappingZoneId
                    );
                  }}
                >
                  Export Data
                </Button>
              </Col>
            </Row>
          ) : null}
        </Fragment>
      )}
    </React.Fragment>
  );
};

NZAPOverview.propTypes = {
  selectedDatabase: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,
  customOptions: PropTypes.object
};

export default observer(NZAPOverview);
